import type { paths } from "@api/schema";
import type { Position } from "@board/positions";
import type { ClinicRosterToday } from "@perception/perceptionClient";
import type {
  OrderRoute,
  OrderStatus,
  SpriteTraits,
} from "@scribenote/brian-design-system";
import { toView, type View } from "@views/viewsClient";

import { type DisplayBoardVisit, toClinicBoardVisits } from "./clinicBoardView";
import {
  type TreatmentPatient,
  toTreatmentPatients,
} from "./treatmentBoardView";

/** Mirrors brian `modules/display/view_schemas.py` `DisplayView`; change both together. */
type DisplayViewOut =
  paths["/display/me/view"]["get"]["responses"][200]["content"]["application/json"];
type ExamRoomViewOut = Extract<DisplayViewOut, { kind: "exam_room" }>;
type SheetOut = ExamRoomViewOut["sheets"][number];
type ConfirmedSheetOut = Extract<SheetOut, { state: "confirmed" }>;
type HeaderOut = SheetOut["header"];
type WeightOut = NonNullable<ConfirmedSheetOut["weight"]>;
type RequestedViewOut = NonNullable<ExamRoomViewOut["requested_view"]>;
type RoomQuestionOut = NonNullable<ExamRoomViewOut["question"]>;

interface ViewSignalment {
  species: string | null;
  breed: string | null;
  color: string | null;
  sex: string | null;
  reproductiveStatus: string | null;
  /** brian's `age_on` text: "4 y", "~9 y", "7 mo". */
  age: string | null;
}

export interface SheetHeader {
  patientId: string;
  name: string;
  surname: string | null;
  signalment: ViewSignalment;
  sprite: SpriteTraits;
  scheduledStart: string | null;
  dvmName: string | null;
}

export interface Weight {
  /** The recorded decimal, verbatim. */
  value: string;
  unit: string;
  observedAt: string;
}

export interface LastVisit {
  visitId: string;
  date: string;
  summary: string | null;
  weight: Weight | null;
}

export interface Owner {
  name: string;
  phone: string | null;
  email: string | null;
}

export interface PlanLine {
  orderId: string;
  chip: OrderStatus;
  text: string;
  route: OrderRoute;
  at: string;
}

export interface PendingSheet {
  state: "pending";
  visitId: string;
  header: SheetHeader;
  position: Position;
}

export interface ConfirmedSheet {
  state: "confirmed";
  visitId: string;
  header: SheetHeader;
  position: Position;
  weight: Weight | null;
  awaySince: string | null;
  awayIn: string | null;
  reason: string | null;
  history: string | null;
  lastVisit: LastVisit | null;
  owner: Owner | null;
  plan: PlanLine[];
}

/** Pending carries the header only: brian withholds the record until staff confirm. */
export type Sheet = PendingSheet | ConfirmedSheet;

/** A view asked for by voice; brian sends it only while its patient is confirmed for the room. */
export interface RequestedView {
  kind: RequestedViewOut["view_kind"];
  patientId: string;
  patientName: string;
  requestedAt: string;
  /** The patient's Summary & Alerts view, whatever the kind. */
  summary: View | null;
}

export interface QuestionCandidate {
  rank: number;
  label: string;
  visitId: string;
  patientId: string;
  sprite: SpriteTraits;
}

/** Brian's which-pet question asked in this room (BRI-379): names and sprites only, no buttons. */
export interface RoomQuestion {
  questionId: string;
  revision: number;
  prompt: string;
  askedAt: string;
  deadlineAt: string;
  candidates: QuestionCandidate[];
}

export type DisplayView =
  | {
      kind: "exam_room";
      roomId: string;
      roomName: string;
      sheets: Sheet[];
      requestedView: RequestedView | null;
      question: RoomQuestion | null;
    }
  | {
      kind: "clinic_board";
      visits: DisplayBoardVisit[];
      roster: ClinicRosterToday;
    }
  | { kind: "treatment_board"; patients: TreatmentPatient[] }
  | { kind: "unsupported" };

const toHeader = (h: HeaderOut): SheetHeader => ({
  patientId: h.patient_uuid,
  name: h.name,
  surname: h.surname,
  signalment: {
    species: h.signalment.species,
    breed: h.signalment.breed,
    color: h.signalment.color,
    sex: h.signalment.sex,
    reproductiveStatus: h.signalment.reproductive_status,
    age: h.signalment.age,
  },
  sprite: h.sprite,
  scheduledStart: h.booking.scheduled_start,
  dvmName: h.booking.dvm_name,
});

const toWeight = (w: WeightOut | null): Weight | null =>
  w && {
    value: String(w.value),
    unit: w.unit,
    observedAt: w.observed_at,
  };

const toSheet = (s: SheetOut): Sheet =>
  s.state === "pending"
    ? {
        state: "pending",
        visitId: s.visit_uuid,
        header: toHeader(s.header),
        position: s.position,
      }
    : {
        state: "confirmed",
        visitId: s.visit_uuid,
        header: toHeader(s.header),
        position: s.position,
        weight: toWeight(s.weight),
        awaySince: s.away_since,
        awayIn: s.away_in,
        reason: s.reason,
        history: s.history,
        lastVisit: s.last_visit && {
          visitId: s.last_visit.visit_uuid,
          date: s.last_visit.date,
          summary: s.last_visit.summary,
          weight: toWeight(s.last_visit.weight),
        },
        owner: s.owner && {
          name: s.owner.name,
          phone: s.owner.phone,
          email: s.owner.email,
        },
        plan: s.plan.map((line) => ({
          orderId: line.order_uuid,
          chip: line.chip,
          text: line.text,
          route: line.route,
          at: line.at,
        })),
      };

const toRequestedView = (r: RequestedViewOut): RequestedView => ({
  kind: r.view_kind,
  patientId: r.patient_uuid,
  patientName: r.patient_name,
  requestedAt: r.requested_at,
  summary: r.summary && toView(r.summary),
});

const toRoomQuestion = (q: RoomQuestionOut): RoomQuestion => ({
  questionId: q.question_uuid,
  revision: q.revision,
  prompt: q.prompt,
  askedAt: q.asked_at,
  deadlineAt: q.deadline_at,
  candidates: q.candidates.map((c) => ({
    rank: c.rank,
    label: c.label,
    visitId: c.visit_uuid,
    patientId: c.patient_uuid,
    sprite: c.sprite,
  })),
});

export const toDisplayView = (v: DisplayViewOut): DisplayView => {
  if (v.kind === "exam_room")
    return {
      kind: "exam_room",
      roomId: v.room_uuid,
      roomName: v.room_name,
      sheets: v.sheets.map(toSheet),
      requestedView: v.requested_view
        ? toRequestedView(v.requested_view)
        : null,
      question: v.question ? toRoomQuestion(v.question) : null,
    };
  if (v.kind === "clinic_board")
    return {
      kind: "clinic_board",
      visits: toClinicBoardVisits(v),
      roster: v.roster,
    };
  if (v.kind === "treatment_board")
    return { kind: "treatment_board", patients: toTreatmentPatients(v) };
  return { kind: "unsupported" };
};
